import React, { useState } from 'react';
import useToastWithSound from '../../hooks/useToastWithSound';
import api from '../../ui/api';
import useData from '../useData';
import useDirtyForm from '../useDirtyForm';
import { useNavIntent } from '../navIntent';
import PageShell, { Button } from '../../ui/PageShell';
import Table from '../../ui/Table';
import Modal, { ConfirmModal } from '../../ui/Modal';
import { FormGrid, Field, Input, Select, Checkbox } from '../../ui/Form';
import HelpButton from '../help/HelpButton';
import EmptyGuide from '../help/EmptyGuide';

/**
 * Grades / Subjects / Batches share one shape — a titled list with
 * create, edit and delete — so they share one component instead of
 * three near-identical files. Sections has extra fields and lives
 * below in its own component.
 */
function SimpleCrud({
  title, description, addLabel, load, create, update, remove, columns, fields, emptyForm, guide, help, empty,
}) {
  const toast = useToastWithSound();
  const { data, loading, error, reload } = useData(load, []);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const dirty = useDirtyForm(form);

  const openNew = () => { setForm(emptyForm); dirty.reset(emptyForm); setEditing({}); };
  const openEdit = (row) => {
    const f = {};
    Object.keys(emptyForm).forEach((k) => { f[k] = row[k] ?? emptyForm[k]; });
    setForm(f);
    dirty.reset(f);
    setEditing(row);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) { await update(editing.id, form); toast.success('تم التحديث'); }
      else { await create(form); toast.success('تمت الإضافة'); }
      setEditing(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await remove(toDelete.id);
      toast.success('تم الحذف');
      setToDelete(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <PageShell
      title={title}
      description={description}
      actions={(
        <>
          {help && <HelpButton guides={help} />}
          <Button onClick={openNew} data-guide={`${guide}-add`}>+ {addLabel}</Button>
        </>
      )}
    >
      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <Table
        guide={`${guide}-table`}
        keyField="id"
        rows={data || []}
        loading={loading}
        emptyText={empty ? <EmptyGuide text={empty.text} guide={empty.guide} /> : 'لا توجد بيانات'}
        onEdit={openEdit}
        onDelete={remove ? (row) => setToDelete(row) : undefined}
        columns={columns}
      />

      {editing && (
        <Modal
          open
          size="md"
          title={editing.id ? `تعديل: ${editing.title || ''}` : addLabel}
          onClose={() => setEditing(null)}
          confirmClose={dirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button variant="success" onClick={save} loading={saving} data-guide="modal-save">حفظ</Button>
            </>
          )}
        >
          <form onSubmit={save}>
            <FormGrid columns={2}>{fields(form, setForm)}</FormGrid>
          </form>
        </Modal>
      )}

      <ConfirmModal
        open={!!toDelete}
        title="تأكيد الحذف"
        message={`سيتم حذف «${toDelete?.title || ''}». لا يمكن الحذف إذا كان مستخدماً في النظام.`}
        confirmText="حذف"
        busy={busy}
        onCancel={() => setToDelete(null)}
        onConfirm={doDelete}
      />
    </PageShell>
  );
}

const gradeBody = (f) => ({
  ...f,
  sort_order: Number(f.sort_order) || 0,
  is_primary_style: f.is_primary_style ? 1 : 0,
});

export function Grades() {
  return (
    <SimpleCrud
      title="الصفوف"
      description="صفوف المدرسة وترتيبها"
      guide="grades"
      help={['primary-setup']}
      addLabel="صف جديد"
      load={() => api.grades()}
      create={(f) => api.createGrade(gradeBody(f))}
      update={(id, f) => api.updateGrade(id, gradeBody(f))}
      remove={(id) => api.deleteGrade(id)}
      emptyForm={{ title: '', sort_order: 0, is_primary_style: false }}
      columns={[
        { key: 'title', label: 'الصف', width: 2, filterable: false, render: (r) => <strong>{r.title}</strong> },
        { key: 'sort_order', label: 'الترتيب', nowrap: true, align: 'center', filterable: false },
        { key: 'is_primary_style', label: 'نمط ابتدائي', nowrap: true, align: 'center',
          render: (r) => (
            <span className={`ui-badge ${r.is_primary_style ? 'ui-badge-info' : 'ui-badge-neutral'}`}>
              {r.is_primary_style ? 'معلم واحد' : 'معلم لكل مادة'}
            </span>
          ) },
      ]}
      fields={(form, setForm) => (
        <>
          <Field label="اسم الصف" required>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </Field>
          <Field label="الترتيب" hint="يحدد ترتيب العرض">
            <Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} />
          </Field>
          <Field label="النمط" span guide="grade-primary">
            <Checkbox
              label="نمط ابتدائي (معلم واحد لكل المواد)"
              checked={!!form.is_primary_style}
              onChange={(e) => setForm((f) => ({ ...f, is_primary_style: e.target.checked }))}
            />
          </Field>
        </>
      )}
    />
  );
}

export function Subjects() {
  return (
    <SimpleCrud
      title="المواد"
      description="قائمة المواد الدراسية"
      guide="subjects"
      help={['subject-add']}
      empty={{ text: 'لا توجد مواد بعد', guide: 'subject-add' }}
      addLabel="مادة جديدة"
      load={() => api.subjects()}
      create={(f) => api.createSubject(f)}
      update={(id, f) => api.updateSubject(id, f)}
      remove={(id) => api.deleteSubject(id)}
      emptyForm={{ title: '' }}
      columns={[
        { key: 'title', label: 'المادة', width: 3, filterable: false, render: (r) => <strong>{r.title}</strong> },
      ]}
      fields={(form, setForm) => (
        <Field label="اسم المادة" required span guide="subject-title">
          <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
        </Field>
      )}
    />
  );
}

export function Batches() {
  return (
    <SimpleCrud
      title="الأفواج"
      description="الفوج الصباحي والمسائي"
      guide="batches"
      addLabel="فوج جديد"
      load={() => api.batches()}
      create={(f) => api.createBatch(f)}
      update={(id, f) => api.updateBatch(id, f)}
      remove={null}
      emptyForm={{ title: '' }}
      columns={[
        { key: 'title', label: 'الفوج', width: 3, filterable: false, render: (r) => <strong>{r.title}</strong> },
      ]}
      fields={(form, setForm) => (
        <Field label="اسم الفوج" required span>
          <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
        </Field>
      )}
    />
  );
}

/** Sections need grade/batch pickers (the year is fixed by the server), so it doesn't use SimpleCrud. */
export function Sections() {
  const toast = useToastWithSound();
  const { data, loading, error, reload } = useData(async () => {
    const [sections, grades, batches, teachers] = await Promise.all([
      api.sections(), api.grades(), api.batches(), api.teachers(),
    ]);
    return { sections, grades, batches, teachers };
  }, []);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', grade_id: '', batch_id: '',
    homeroom_teacher_id: '', status: 'active',
  });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const dirty = useDirtyForm(form);

  const grades = data?.grades || [];
  const batches = data?.batches || [];
  const teachers = (data?.teachers || []).filter((t) => t.status !== 'inactive');

  const rows = (data?.sections || []).map((s) => ({
    ...s,
    grade_title: grades.find((g) => g.id === s.grade_id)?.title || '—',
    batch_title: batches.find((b) => b.id === s.batch_id)?.title || '—',
    homeroom_label: s.homeroom_teacher_name || '—',
  }));

  const openNew = () => {
    const next = {
      title: '', grade_id: grades[0]?.id || '', batch_id: batches[0]?.id || '',
      homeroom_teacher_id: '', status: 'active', works_friday: false, works_saturday: false,
    };
    setForm(next);
    dirty.reset(next);
    setEditing({});
  };

  const openEdit = (s) => {
    const next = {
      title: s.title, grade_id: s.grade_id, batch_id: s.batch_id,
      homeroom_teacher_id: s.homeroom_teacher_id || '',
      status: s.status,
      works_friday: !!s.works_friday, works_saturday: !!s.works_saturday,
    };
    setForm(next);
    dirty.reset(next);
    setEditing(s);
  };

  // Navbar search → «تعديل الشعبة».
  useNavIntent('sections', (it) => {
    if (!data) return false;
    const s = rows.find((r) => r.id === it.sectionId);
    if (s) openEdit(s);
  }, [data]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('اسم الشعبة مطلوب');
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        grade_id: Number(form.grade_id),
        batch_id: Number(form.batch_id),
        // Empty means "no homeroom teacher" (grades 7–12), and must be
        // sent as null so an existing one can actually be cleared.
        homeroom_teacher_id: form.homeroom_teacher_id ? Number(form.homeroom_teacher_id) : null,
        status: form.status,
        works_friday: form.works_friday ? 1 : 0,
        works_saturday: form.works_saturday ? 1 : 0,
      };
      if (editing.id) { await api.updateSection(editing.id, payload); toast.success('تم التحديث'); }
      else { await api.createSection(payload); toast.success('تمت الإضافة'); }
      setEditing(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.deleteSection(toDelete.id);
      toast.success('تم الحذف');
      setToDelete(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <PageShell title="الشعب" description="شعب المدرسة وأعداد طلابها"
      actions={(
        <>
          <HelpButton guides={['section-create', 'primary-setup']} />
          <Button onClick={openNew} disabled={loading} data-guide="sections-add">+ شعبة جديدة</Button>
        </>
      )}>
      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <Table
        guide="sections-table"
        keyField="id"
        rows={rows}
        loading={loading}
        statusKey="status"
        emptyText={<EmptyGuide text="لا توجد شعب بعد" guide="section-create" />}
        searchPlaceholder="ابحث عن شعبة..."
        onEdit={openEdit}
        onDelete={(s) => setToDelete(s)}
        columns={[
          { key: 'title', label: 'الشعبة', width: 2, filterable: false, render: (r) => <strong>{r.title}</strong> },
          { key: 'grade_title', label: 'الصف' },
          { key: 'batch_title', label: 'الفوج', nowrap: true },
          { key: 'homeroom_label', label: 'معلم الصف' },
          { key: 'student_count', label: 'الطلاب', nowrap: true, align: 'center', filterable: false,
            render: (r) => <strong>{r.student_count ?? 0}</strong> },
          { key: 'works_friday', label: 'دوام إضافي', nowrap: true, align: 'center', filterable: false,
            render: (r) => {
              const days = [r.works_friday && 'الجمعة', r.works_saturday && 'السبت'].filter(Boolean);
              return days.length ? <span className="ui-badge ui-badge-info">{days.join(' و')}</span> : '—';
            } },
          { key: 'status', label: 'الحالة', nowrap: true, align: 'center',
            badge: (v) => (v === 'active' ? 'success' : 'neutral'),
            formatValue: (v) => (v === 'active' ? 'نشطة' : 'مغلقة') },
        ]}
      />

      {editing && (
        <Modal open size="md" title={editing.id ? `تعديل: ${editing.title}` : 'شعبة جديدة'}
          onClose={() => setEditing(null)}
          confirmClose={dirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button variant="success" onClick={save} loading={saving} data-guide="modal-save">حفظ</Button>
            </>
          )}>
          <form onSubmit={save}>
            <FormGrid columns={2}>
              <Field label="اسم الشعبة" required guide="section-title"><Input value={form.title} onChange={set('title')} placeholder="أولى م" /></Field>
              <Field label="الصف" required guide="section-grade">
                <Select value={form.grade_id} onChange={set('grade_id')} placeholder="اختر الصف"
                  options={grades.map((g) => ({ value: g.id, label: g.title }))} />
              </Field>
              <Field label="الفوج" required guide="section-batch">
                <Select value={form.batch_id} onChange={set('batch_id')} placeholder="اختر الفوج"
                  options={batches.map((b) => ({ value: b.id, label: b.title }))} />
              </Field>
              {/* معلم الصف: one teacher covering every subject in this
                  section (grades 1–6). Any subject that instead has its
                  own assignment — English and the like — stays with that
                  specialist. Grades 7–12 leave this empty. */}
              <Field
                label="معلم الصف"
                span
                guide="section-homeroom"
                hint="للصفوف 1–6: معلم واحد يغطي كل المواد التي ليس لها توكيل خاص. اتركه فارغاً للصفوف الأعلى."
              >
                <Select value={form.homeroom_teacher_id} onChange={set('homeroom_teacher_id')}
                  placeholder="بدون معلم صف"
                  options={teachers.map((t) => ({ value: t.id, label: t.name }))} />
              </Field>
              <Field label="دوام إضافي" span hint="الجمعة والسبت عطلة لكل الشعب إلا ما تحدده هنا">
                <Checkbox
                  label="دوام يوم الجمعة (يُؤخذ التفقّد)"
                  checked={!!form.works_friday}
                  onChange={(e) => setForm((f) => ({ ...f, works_friday: e.target.checked }))}
                />
                <Checkbox
                  label="دوام يوم السبت (يُؤخذ التفقّد)"
                  checked={!!form.works_saturday}
                  onChange={(e) => setForm((f) => ({ ...f, works_saturday: e.target.checked }))}
                />
              </Field>
              <Field label="الحالة" span>
                <Select value={form.status} onChange={set('status')}
                  options={[{ value: 'active', label: 'نشطة' }, { value: 'inactive', label: 'مغلقة' }]} />
              </Field>
            </FormGrid>
          </form>
        </Modal>
      )}

      <ConfirmModal
        open={!!toDelete}
        title="حذف الشعبة"
        message={`سيتم حذف «${toDelete?.title || ''}». لا يمكن الحذف إذا كان فيها طلاب مسجّلون.`}
        confirmText="حذف"
        busy={busy}
        onCancel={() => setToDelete(null)}
        onConfirm={doDelete}
      />
    </PageShell>
  );
}
